/**
 * @deprecated Use `caseDocumentImportApi` from `./case-document-import` instead.
 *
 * Legacy "extraction scope" API kept as a thin adapter while the older modals
 * (ExtractionScopeModal / StructuralExtractionModal) are still in the tree.
 * It maps the legacy shape onto the new `/v1/lake-documents/{id}/imports`
 * endpoints, stripping fields the new backend no longer supports
 * (`selected_chunk_ids`, `tabular_config`, `manual_schema`). Submitting an
 * unsupported `scope_type` ("chunks" or "tabular") raises at the call site
 * instead of round-tripping to the server.
 *
 * New consumers SHOULD use the canonical client in
 * `./case-document-import.ts`.
 */

import { caseDocumentImportApi } from './case-document-import'
import type { WizardConfiguration } from './cases'
import { apiClient } from './client'

// ---------------------------------------------------------------------------
// Tabular config types — retained so legacy modals continue to compile, but
// the backend ignores tabular_config payloads.
// ---------------------------------------------------------------------------

export interface TabularBatch {
  rows: number[]
  columns: number[]
}

export interface TabularConfig {
  headers: string[]
  header_row_index: number | null
  batches: TabularBatch[]
}

// ---------------------------------------------------------------------------
// Core scope types (legacy shape — kept for source compatibility)
// ---------------------------------------------------------------------------

export interface ExtractionScope {
  id: string
  document_id: string
  case_id: string
  scope_type: 'all' | 'chunks' | 'tabular' | 'pages'
  selected_chunk_ids: string[] | null
  tabular_config: TabularConfig | null
  selected_pages: number[] | null
  /** Tabular scope: column names kept (non-null only when scope_type='tabular'). */
  selected_columns: string[] | null
  /** Tabular scope: 0-based `__row_index` values kept (non-null only when scope_type='tabular'). */
  selected_rows: number[] | null
  scope_note: string | null
  manual_schema: WizardConfiguration | null
  created_at: string
  created_by: string | null
  reviewed_at: string | null
  reviewed_by: string | null
  case_name: string
}

export interface CreateScopeRequest {
  case_id: string
  scope_type: 'all' | 'chunks' | 'tabular' | 'pages'
  selected_chunk_ids?: string[] | null
  tabular_config?: TabularConfig | null
  selected_pages?: number[] | null
  scope_note?: string | null
}

export interface ResolvePageScopeRequest {
  page_numbers: number[]
}

export interface ResolvePageScopeResponse {
  chunk_ids: string[]
  chunk_count: number
  boundary_chunks: string[]
}

function adaptImport(imp: {
  id: string
  case_document_id: string
  lake_document_id: string
  case_id: string
  scope_type: string
  selected_pages: number[] | null
  selected_columns?: string[] | null
  selected_rows?: number[] | null
  scope_note: string | null
  created_at: string
  created_by: string | null
  reviewed_at: string | null
  reviewed_by: string | null
  case_name?: string
}): ExtractionScope {
  return {
    id: imp.id,
    // Legacy callers expect `document_id` to be the case-side document id.
    document_id: imp.case_document_id,
    case_id: imp.case_id,
    scope_type: imp.scope_type as ExtractionScope['scope_type'],
    selected_chunk_ids: null,
    tabular_config: null,
    selected_pages: imp.selected_pages,
    selected_columns: imp.selected_columns ?? null,
    selected_rows: imp.selected_rows ?? null,
    scope_note: imp.scope_note,
    manual_schema: null,
    created_at: imp.created_at,
    created_by: imp.created_by,
    reviewed_at: imp.reviewed_at,
    reviewed_by: imp.reviewed_by,
    case_name: imp.case_name ?? '',
  }
}

export const extractionScopeApi = {
  /**
   * Create or replace an extraction scope. Maps to the new `/imports` upsert.
   * Rejects `chunks` and `tabular` scope types — these are no longer supported
   * by the backend.
   */
  async create(documentId: string, request: CreateScopeRequest): Promise<ExtractionScope> {
    if (request.scope_type !== 'all' && request.scope_type !== 'pages') {
      throw new Error(
        `extractionScopeApi.create: scope_type '${request.scope_type}' is no longer supported. ` +
          'Use scope_type="all" or "pages".'
      )
    }
    const imp = await caseDocumentImportApi.upsert(documentId, {
      case_id: request.case_id,
      scope_type: request.scope_type,
      selected_pages: request.selected_pages ?? null,
      scope_note: request.scope_note ?? null,
    })
    return adaptImport(imp)
  },

  async list(documentId: string): Promise<ExtractionScope[]> {
    const imports = await caseDocumentImportApi.list(documentId)
    return imports.map(adaptImport)
  },

  async delete(documentId: string, scopeId: string): Promise<void> {
    await caseDocumentImportApi.remove(documentId, scopeId)
  },

  /**
   * @deprecated The chunk-recycling model is gone; this method has no
   * backend counterpart. Calls will throw.
   */
  async resolvePageScope(
    _documentId: string,
    _request: ResolvePageScopeRequest
  ): Promise<ResolvePageScopeResponse> {
    throw new Error(
      'resolvePageScope is no longer supported. Scope is applied at the read step ' +
        'of the case extraction pipeline; selected_pages are stored on the import directly.'
    )
  },

  /**
   * Find the scope for a specific (lake document, case) pair, or null.
   */
  async getByCase(documentId: string, caseId: string): Promise<ExtractionScope | null> {
    const imp = await caseDocumentImportApi.getByCase(documentId, caseId)
    return imp ? adaptImport(imp) : null
  },

  /**
   * @deprecated `manual_schema` has been removed from the import record.
   * Schema authoring lives in the case workspace.
   */
  async updateSchema(
    _documentId: string,
    _scopeId: string,
    _manualSchema: WizardConfiguration | null
  ): Promise<ExtractionScope> {
    throw new Error(
      'updateSchema is no longer supported on imports. Build schemas inside the case workspace.'
    )
  },

  async markAsReviewed(documentId: string, scopeId: string): Promise<ExtractionScope> {
    const imp = await caseDocumentImportApi.markAsReviewed(documentId, scopeId)
    return adaptImport({ ...imp, case_name: undefined })
  },
}

// Suppress "imported but never used" for apiClient (legacy import retained for
// minimum diff if a future revision needs direct access).
void apiClient
